/**
 * StepUpAuthModal (Sprint 6 of FI Staff Authn/Z arc).
 *
 * Sensitive-operation step-up gate. Composes `@aaimplatform/ui` Dialog,
 * Alert, Input, Button, and RadioGroup primitives -- zero local
 * re-implementations per `.claude/rules/ui-package.md`.
 *
 * The modal mints a short-lived JWT against the canonical
 * `/api/v1/auth/step-up-token` endpoint (Sprint 6 spec at
 * `schema-registry/auth-step-up.openapi.yaml`). The minted token is
 * bound to `{operation, subjectUserId, actorUserId}` via the spec's
 * `stepUpOpHash` claim; replaying it against a different operation or
 * subject fails at the upstream guard.
 *
 * Sprint 5 fallback paths (placeholder POST against the unspecced
 * `/api/v1/auth/step-up`, "Continue Without" button) are REMOVED. The
 * mint endpoint is real; a failed mint surfaces inline and the operator
 * must retry or cancel.
 *
 * On successful mint the token is cached by `operationHash` for a 5
 * minute window (matches the spec's 300-second `expiresAt`). The cache
 * is module-scoped so a single verification covers preview + commit in
 * the same edit-roles flow.
 */

import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  Input,
  RadioGroup,
} from '@aaimplatform/ui';
import {
  ApiResponseError,
  StepUpRequiredError,
  useStepUpToken,
  type StepUpOperation,
  type StepUpProofType,
} from '../../hooks/useStaffLifecycle';

const STEP_UP_TOKEN_TTL_MS = 5 * 60 * 1000;

interface CachedStepUpToken {
  token: string;
  expiresAt: number;
}

const stepUpTokenCache = new Map<string, CachedStepUpToken>();

/**
 * Lookup helper exposed for tests + sibling components that need to
 * short-circuit the modal when a recent token is still valid.
 */
export function getCachedStepUpToken(operationHash: string): string | null {
  if (!operationHash) return null;
  const cached = stepUpTokenCache.get(operationHash);
  if (!cached) return null;
  if (cached.expiresAt <= Date.now()) {
    stepUpTokenCache.delete(operationHash);
    return null;
  }
  return cached.token;
}

/**
 * Test-only: clear the in-memory cache so a fresh modal always issues a
 * new verification request. Components SHOULD NOT call this in normal
 * runtime paths.
 */
export function clearStepUpTokenCache(): void {
  stepUpTokenCache.clear();
}

export interface StepUpAuthModalProps {
  /** Controls modal visibility. Mirrors React Aria modal contract. */
  open: boolean;
  /** Visibility change callback (close button, Escape key, backdrop). */
  onOpenChange: (open: boolean) => void;
  /**
   * Canonical operation slug per the auth-step-up spec. The mint server
   * uses this in the opHash so the token cannot be replayed against a
   * different operation.
   */
  operation: StepUpOperation;
  /**
   * Target user the operation applies to. The mint server uses this in
   * the opHash so the token cannot be replayed against a different
   * subject.
   */
  subjectUserId: string;
  /** Operator-facing label for the operation (e.g. "Suspend Erin Davies"). */
  operationLabel: string;
  /**
   * Cache key. Caller derives a stable hash from `${operation}:${subjectUserId}`
   * so a successful verification covers the same operation on retry
   * within the TTL.
   */
  operationHash?: string;
  /** Success callback. Receives the minted step-up token. */
  onVerified: (stepUpToken: string) => void;
  /** Cancel callback. Invoked when the operator dismisses the modal. */
  onCancel: () => void;
}

/**
 * Render a Retry-After wait for an operator: whole minutes once past a minute,
 * seconds below that. Rounds UP so the message never invites a retry that the
 * server would still refuse.
 */
function formatWait(seconds: number): string {
  if (seconds < 60) return `${Math.max(1, Math.ceil(seconds))} seconds`;
  const minutes = Math.ceil(seconds / 60);
  return minutes === 1 ? 'a minute' : `${minutes} minutes`;
}

/**
 * Turn a mint failure into text an operator can act on (GH #2461 deliverable 3).
 *
 * The rule this enforces: a raw `Error.message` is NEVER rendered. The defect
 * that produced this function was a browser-internal TypeError
 * ("Failed to execute 'clone' on 'Request': Request body is already used")
 * reaching the operator under the heading "Verification failed", telling them
 * nothing about what to do. Server prose for 4xx/5xx is developer-facing too
 * ("subjectUserId is required and must be a UUID"), so it is logged, not shown.
 *
 * Statuses are the ones `schema-registry/auth-step-up.openapi.yaml` 1.1.0
 * declares on POST /auth/step-up-token: 400, 401, 403, 404, 409, 429 and 500.
 * The 403 carries one of two codes that need different actions: NOT_AUTHORIZED
 * (the account lacks the operation's permission or role) and ACTOR_NOT_ACTIVE
 * (the caller's membership in the institution is invited, suspended or
 * offboarded). The 409 PLATFORM_ADMIN_IMPERSONATION_REQUIRED tells a platform
 * administrator to impersonate the pledge's institution before deciding on it
 * (arc A kj-03e E3, operator ruling T24 items 7, 9 and 10).
 *
 * THE 401 SPLIT. The spec declares that status as "Bearer missing or invalid;
 * OR proof verification failed" -- one code for two operator situations that
 * need opposite actions (retype the code / sign in again). The SERVER already
 * separates them: `PROOF_INVALID` (:199, :292, :317) against `AUTH_REQUIRED`
 * (`auth-step-up.routes.ts:137`). So this discriminates on the code, which the
 * spec declares as "Machine-readable error code" without enumerating values.
 * #2461 deliverable 6 (enumerate them in the contract) is still the better
 * fix and is the operator's call; it is not a prerequisite for this.
 */
export function describeMintFailure(err: unknown, proofType: StepUpProofType): string {
  const proofNoun = proofType === 'totp' ? 'code' : 'password';

  if (err instanceof ApiResponseError) {
    if (err.status === 401) {
      switch (err.errorCode) {
        case 'PROOF_INVALID':
          return `That ${proofNoun} was not accepted. Check it and try again.`;
        case 'MFA_NOT_ENROLLED':
          return 'No authenticator app is enrolled for your account. Verify with your password instead.';
        case 'AUTH_REQUIRED':
          return 'Your session has expired. Sign in again, then retry this operation.';
        default:
          return `Verification failed. Either the ${proofNoun} was not accepted or your session expired -- retry, and sign in again if it keeps failing.`;
      }
    }
    if (err.status === 429) {
      // GH #2659: the mint now refuses a brute-forced proof, and the wait it
      // states comes from the server's Retry-After rather than from a guess.
      // This branch previously read "Wait a minute", which was wrong in both
      // directions -- there was no limiter to wait out at all, and the window
      // the limiter enforces is measured in minutes, not one.
      return err.retryAfterSeconds
        ? `Too many verification attempts. Try again in ${formatWait(err.retryAfterSeconds)}.`
        : 'Too many verification attempts. Try again later.';
    }
    if (err.status === 403) {
      if (err.errorCode === 'ACTOR_NOT_ACTIVE') {
        return 'Your membership in this institution is not active. Ask an administrator to reactivate it, then retry.';
      }
      return 'Your account is not authorized to verify this operation.';
    }
    if (err.status === 409 && err.errorCode === 'PLATFORM_ADMIN_IMPERSONATION_REQUIRED') {
      return "Pledge decisions are made in the pledge's institution. Impersonate that institution, then retry.";
    }
    if (err.status === 404) {
      return 'The account this operation applies to was not found.';
    }
    if (err.status === 400) {
      return 'The verification request was rejected as malformed. This is a defect; report it with the operation name.';
    }
    if (err.status >= 500) {
      return 'Verification is temporarily unavailable. Try again shortly.';
    }
    return `Verification failed (HTTP ${err.status}). Try again shortly.`;
  }

  // No status: a network failure, or an exception thrown inside the client
  // before any response existed. Both are unactionable as raw text.
  return 'Verification could not be completed. Check your connection and try again.';
}

export function StepUpAuthModal({
  open,
  onOpenChange,
  operation,
  subjectUserId,
  operationLabel,
  operationHash,
  onVerified,
  onCancel,
}: StepUpAuthModalProps) {
  const [proofType, setProofType] = useState<StepUpProofType>('password');
  const [proofValue, setProofValue] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mintMutation = useStepUpToken();

  // Reset transient state every time the modal closes so a re-open
  // starts fresh.
  useEffect(() => {
    if (!open) {
      setProofType('password');
      setProofValue('');
      setErrorMessage(null);
      mintMutation.reset();
    }
    // mintMutation identity is stable per react-query; only re-run when `open` flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Short-circuit when a cached token is still valid.
  useEffect(() => {
    if (!open) return;
    const cachedToken = operationHash ? getCachedStepUpToken(operationHash) : null;
    if (cachedToken) {
      onVerified(cachedToken);
      onOpenChange(false);
    }
  }, [open, operationHash, onVerified, onOpenChange]);

  const handleSubmit = async () => {
    setErrorMessage(null);
    const trimmed = proofValue.trim();
    if (!trimmed) {
      setErrorMessage(
        proofType === 'totp'
          ? 'Enter the 6-digit code from your authenticator app.'
          : 'Enter your password to verify your identity.',
      );
      return;
    }

    try {
      const response = await mintMutation.mutateAsync({
        operation,
        subjectUserId,
        proof: { type: proofType, value: trimmed },
      });

      if (operationHash && response.stepUpToken) {
        stepUpTokenCache.set(operationHash, {
          token: response.stepUpToken,
          expiresAt: Date.now() + STEP_UP_TOKEN_TTL_MS,
        });
      }
      onVerified(response.stepUpToken);
      onOpenChange(false);
    } catch (err) {
      if (err instanceof StepUpRequiredError) {
        setErrorMessage('Step-up verification rejected. Verify the code and try again.');
        return;
      }
      // The operator gets classified copy; the diagnostic detail survives in the
      // console for anyone with devtools open. Dropping it entirely would trade
      // one debugging problem for another.
      console.error('[step-up] mint failed', err);
      setErrorMessage(describeMintFailure(err, proofType));
    }
  };

  const handleCancel = () => {
    onCancel();
    onOpenChange(false);
  };

  const isSubmitting = mintMutation.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Verify your identity to continue."
      size="sm"
      footer={
        <>
          <Button variant="tertiary" onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              void handleSubmit();
            }}
            disabled={isSubmitting || proofValue.trim().length === 0}
          >
            {isSubmitting ? 'Verifying...' : 'Verify'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm font-sans text-secondary">
          Operation:{' '}
          <span className="font-mono text-primary">{operationLabel}</span>
        </p>

        <RadioGroup
          label="Proof type"
          value={proofType}
          onChange={(next) => {
            setProofType(next as StepUpProofType);
            setProofValue('');
            setErrorMessage(null);
          }}
          options={[
            { value: 'password', label: 'Password' },
            { value: 'totp', label: 'Authenticator code (TOTP)' },
          ]}
        />

        <Input
          label={proofType === 'password' ? 'Password' : 'Authenticator code'}
          type={proofType === 'password' ? 'password' : 'text'}
          autoComplete={proofType === 'password' ? 'current-password' : 'one-time-code'}
          value={proofValue}
          onChange={(e) => {
            setProofValue(e.target.value);
            setErrorMessage(null);
          }}
          placeholder={proofType === 'totp' ? '123456' : ''}
          required
        />

        {errorMessage && (
          <Alert type="error" title="Verification failed">
            {errorMessage}
          </Alert>
        )}
      </div>
    </Dialog>
  );
}

export default StepUpAuthModal;
